'use client';

import { useState } from 'react';
import { Calculator, DollarSign, Percent, TrendingUp, TrendingDown, Clock, Calendar, CreditCard, Zap, Shield, Heart } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { salaryCalculatorContent } from '../../data/salary-calculator-content';

type SalaryBreakdown = {
  basic: number;
  hra: number;
  da: number;
  ta: number;
  ma: number;
  pf: number;
  tax: number;
  otherAllowances: number;
  otherDeductions: number;
  grossSalary: number;
  netSalary: number;
  annualPackage: number;
};

export default function SalaryCalculator() {
  const [formData, setFormData] = useState({
    basicSalary: '',
    hraPercentage: '40',
    daPercentage: '20',
    ta: '2000',
    ma: '1250',
    pfPercentage: '12',
    taxPercentage: '10',
    otherAllowances: '0',
    otherDeductions: '0',
    paymentFrequency: 'monthly',
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [results, setResults] = useState<SalaryBreakdown | null>(null);

  const calculateSalary = () => {
    const basic = parseFloat(formData.basicSalary) || 0;
    const hraPercentage = parseFloat(formData.hraPercentage) / 100;
    const daPercentage = parseFloat(formData.daPercentage) / 100;
    const ta = parseFloat(formData.ta) || 0;
    const ma = parseFloat(formData.ma) || 0;
    const pfPercentage = parseFloat(formData.pfPercentage) / 100;
    const taxPercentage = parseFloat(formData.taxPercentage) / 100;
    const otherAllowances = parseFloat(formData.otherAllowances) || 0;
    const otherDeductions = parseFloat(formData.otherDeductions) || 0;

    const hra = basic * hraPercentage;
    const da = basic * daPercentage;
    const pf = basic * pfPercentage;

    const grossSalary = basic + hra + da + ta + ma + otherAllowances;
    const taxableIncome = grossSalary - pf - otherDeductions;
    const tax = taxableIncome * taxPercentage;

    const netSalary = grossSalary - pf - tax - otherDeductions;
    const annualPackage = formData.paymentFrequency === 'monthly'
      ? grossSalary * 12
      : grossSalary * 12 / 52 * 52; // Assuming 52 weeks in a year

    setResults({
      basic,
      hra,
      da,
      ta,
      ma,
      pf,
      tax,
      otherAllowances,
      otherDeductions,
      grossSalary,
      netSalary,
      annualPackage
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const resetForm = () => {
    setFormData({
      basicSalary: '',
      hraPercentage: '40',
      daPercentage: '20',
      ta: '2000',
      ma: '1250',
      pfPercentage: '12',
      taxPercentage: '10',
      otherAllowances: '0',
      otherDeductions: '0',
      paymentFrequency: 'monthly',
    });
    setResults(null);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-8">
          <ToolPageIntro title="Salary Calculator - Free Online Salary and Income Calculator">
                Calculate take-home pay, taxes, and deductions with our free online salary calculator. Understand your net income and budget effectively.
            </ToolPageIntro>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
              Salary Details
            </h2>

            <div className="space-y-4">
              <div>
                <label htmlFor="basicSalary" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Basic Salary <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    id="basicSalary"
                    name="basicSalary"
                    value={formData.basicSalary}
                    onChange={handleInputChange}
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="e.g. 50000"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="hraPercentage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    HRA (%)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Percent className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      id="hraPercentage"
                      name="hraPercentage"
                      value={formData.hraPercentage}
                      onChange={handleInputChange}
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="40"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="daPercentage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    DA (%)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Percent className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      id="daPercentage"
                      name="daPercentage"
                      value={formData.daPercentage}
                      onChange={handleInputChange}
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="20"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="ta" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Travel Allowance (TA)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <TrendingUp className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      id="ta"
                      name="ta"
                      value={formData.ta}
                      onChange={handleInputChange}
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="2000"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="ma" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Medical Allowance (MA)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Heart className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      id="ma"
                      name="ma"
                      value={formData.ma}
                      onChange={handleInputChange}
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="1250"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="pfPercentage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    PF (%)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Shield className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      id="pfPercentage"
                      name="pfPercentage"
                      value={formData.pfPercentage}
                      onChange={handleInputChange}
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="12"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="taxPercentage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tax (%)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <TrendingDown className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      id="taxPercentage"
                      name="taxPercentage"
                      value={formData.taxPercentage}
                      onChange={handleInputChange}
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="10"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="paymentFrequency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Payment Frequency
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-5 w-5 text-gray-400" />
                  </div>
                  <select
                    id="paymentFrequency"
                    name="paymentFrequency"
                    value={formData.paymentFrequency}
                    onChange={handleInputChange}
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="weekly">Weekly</option>
                    <option value="bi-weekly">Bi-Weekly</option>
                    <option value="semi-monthly">Semi-Monthly</option>
                    <option value="annually">Annually</option>
                  </select>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center"
              >
                {showAdvanced ? 'Hide' : 'Show'} Advanced Options
                <svg
                  className={`ml-1 h-4 w-4 transform transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showAdvanced && (
                <div className="space-y-4 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <div>
                    <label htmlFor="otherAllowances" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Other Allowances
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Zap className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="number"
                        id="otherAllowances"
                        name="otherAllowances"
                        value={formData.otherAllowances}
                        onChange={handleInputChange}
                        className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="otherDeductions" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Other Deductions
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <TrendingDown className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="number"
                        id="otherDeductions"
                        name="otherDeductions"
                        value={formData.otherDeductions}
                        onChange={handleInputChange}
                        className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-4 flex space-x-3">
                <button
                  type="button"
                  onClick={calculateSalary}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-700 dark:hover:bg-blue-800 flex items-center justify-center"
                >
                  <Calculator className="h-5 w-5 mr-2" />
                  Calculate
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
              Salary Breakdown
            </h2>

            {results ? (
              <div className="space-y-6">
                <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg">
                  <div className="text-center">
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-200">Annual Package</p>
                    <p className="text-3xl font-bold text-blue-900 dark:text-white">{formatCurrency(results.annualPackage)}</p>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      {formData.paymentFrequency === 'monthly'
                        ? `${formatCurrency(results.grossSalary)} per month`
                        : formData.paymentFrequency === 'weekly'
                          ? `${formatCurrency(results.grossSalary)} per week`
                          : formData.paymentFrequency === 'bi-weekly'
                            ? `${formatCurrency(results.grossSalary)} every 2 weeks`
                            : formData.paymentFrequency === 'semi-monthly'
                              ? `${formatCurrency(results.grossSalary)} twice a month`
                              : `${formatCurrency(results.grossSalary)} per year`
                      }
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-medium text-gray-700 dark:text-gray-300">Earnings</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Basic Salary</span>
                      <span className="font-medium">{formatCurrency(results.basic)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">HRA ({formData.hraPercentage}%)</span>
                      <span className="font-medium">{formatCurrency(results.hra)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">DA ({formData.daPercentage}%)</span>
                      <span className="font-medium">{formatCurrency(results.da)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Travel Allowance (TA)</span>
                      <span className="font-medium">{formatCurrency(results.ta)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Medical Allowance (MA)</span>
                      <span className="font-medium">{formatCurrency(results.ma)}</span>
                    </div>
                    {results.otherAllowances > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Other Allowances</span>
                        <span className="font-medium">{formatCurrency(results.otherAllowances)}</span>
                      </div>
                    )}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                      <div className="flex justify-between font-semibold">
                        <span>Gross Salary</span>
                        <span>{formatCurrency(results.grossSalary)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-medium text-gray-700 dark:text-gray-300">Deductions</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Provident Fund ({formData.pfPercentage}%)</span>
                      <span className="font-medium text-red-600 dark:text-red-400">-{formatCurrency(results.pf)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Tax ({formData.taxPercentage}%)</span>
                      <span className="font-medium text-red-600 dark:text-red-400">-{formatCurrency(results.tax)}</span>
                    </div>
                    {results.otherDeductions > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Other Deductions</span>
                        <span className="font-medium text-red-600 dark:text-red-400">-{formatCurrency(results.otherDeductions)}</span>
                      </div>
                    )}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                      <div className="flex justify-between font-semibold">
                        <span>Total Deductions</span>
                        <span className="text-red-600 dark:text-red-400">-{formatCurrency(results.pf + results.tax + results.otherDeductions)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-green-800 dark:text-green-200">Net Take Home Salary</p>
                      <p className="text-sm text-green-700 dark:text-green-300">After all deductions</p>
                    </div>
                    <p className="text-2xl font-bold text-green-900 dark:text-white">{formatCurrency(results.netSalary)}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Calculator className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No calculations yet</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Enter your salary details and click "Calculate" to see the breakdown.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">About This Calculator</h2>
          <div className="prose dark:prose-invert max-w-none">
            <p>
              This salary calculator helps you understand your take-home pay after various deductions like taxes,
              provident fund (PF), and other allowances. The calculations are based on standard Indian salary
              components and tax regulations.
            </p>
            <h3 className="text-lg font-medium mt-4">Salary Components Explained:</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Basic Salary:</strong> The core salary (usually 40-50% of CTC)</li>
              <li><strong>HRA (House Rent Allowance):</strong> For housing expenses (usually 40-50% of basic)</li>
              <li><strong>DA (Dearness Allowance):</strong> Cost of living adjustment (usually 20-30% of basic)</li>
              <li><strong>TA (Travel Allowance):</strong> For commuting expenses</li>
              <li><strong>MA (Medical Allowance):</strong> For medical expenses</li>
              <li><strong>PF (Provident Fund):</strong> Retirement savings (12% of basic)</li>
            </ul>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
              Note: This calculator provides estimates only. For exact figures, please consult with your HR or a tax professional.
            </p>
          </div>
        </div>
      </div>

      {/* Informational Content */}
      <div className="mt-12">
        <InformationalContent
          title={salaryCalculatorContent.title}
          description={salaryCalculatorContent.description}
          content={salaryCalculatorContent.content}
          faqs={salaryCalculatorContent.faqs}
          relatedTools={salaryCalculatorContent.relatedTools}
        />
      </div>
    </div>
  );
}
